import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "sonner";
import { ThemeProvider } from "@/components/ThemeProvider";
import { IndustryProvider } from "@/components/IndustryContext";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import RecoverPage from "@/pages/RecoverPage";
import VerifyEmailPage from "@/pages/VerifyEmailPage";
import DashboardPage from "@/pages/DashboardPage";
import SettingsPage from "@/pages/SettingsPage";
import AppointmentsPage from "@/pages/AppointmentsPage";
import PatientsPage from "@/pages/PatientsPage";
import FinancePage from "@/pages/FinancePage";
import FilesPage from "@/pages/FilesPage";
import ImportPage from "@/pages/ImportPage";
import PricingPage from "@/pages/PricingPage";
import AdminPage from "@/pages/AdminPage";
import LandingPage from "@/pages/LandingPage";
import ProfilePage from "@/pages/ProfilePage";
import ProfileEditPage from "@/pages/ProfileEditPage";
import PrivacyPage from "@/pages/PrivacyPage";
import OfferPage from "@/pages/OfferPage";
import BookingPage from "@/pages/BookingPage";
import LiveQueuePage from "@/pages/LiveQueuePage";
import MessengerPage from "@/pages/MessengerPage";
import ClinicPage from "@/pages/ClinicPage";
import AnalyticsPage from "@/pages/AnalyticsPage";
import PatientBoardPage from "@/pages/PatientBoardPage";
import AssistantPage from "@/pages/AssistantPage";
import api from "@/api";

const MAIN_HOSTS = ['propraxis.ru', 'www.propraxis.ru', 'localhost'];

function App() {
  const [tenantMode, setTenantMode] = useState(null); // null=checking, false=main, {type,slug}=custom domain
  const hostname = window.location.hostname;

  useEffect(() => {
    // Check if this is a custom domain
    const isMain = MAIN_HOSTS.includes(hostname) ||
      hostname.startsWith('localhost') ||
      hostname.startsWith('127.') ||
      hostname.match(/^\d+\.\d+\.\d+\.\d+$/);

    if (isMain) {
      setTenantMode(false);
      return;
    }

    // Custom domain — resolve tenant
    api.get(`/public/tenant/resolve?domain=${encodeURIComponent(hostname)}`)
      .then(({ data }) => setTenantMode(data))
      .catch(() => setTenantMode(false)); // Fallback to main if resolve fails
  }, [hostname]);

  // Still resolving
  if (tenantMode === null) {
    return (
      <div style={{ background: '#0c0a09', color: '#78716c', height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <p>Загрузка...</p>
      </div>
    );
  }

  // Custom domain resolved — show only public page
  if (tenantMode && tenantMode.slug) {
    if (tenantMode.type === 'organization') {
      return (
        <ErrorBoundary>
          <ThemeProvider>
            <div className="App">
              <Toaster richColors position="top-right" />
              <BrowserRouter>
                <Routes>
                  <Route path="/p/:slug" element={<ProfilePage />} />
                  <Route path="/book/:linkId" element={<BookingPage />} />
                  <Route path="*" element={<ClinicPage slug={tenantMode.slug} />} />
                </Routes>
              </BrowserRouter>
            </div>
          </ThemeProvider>
        </ErrorBoundary>
      );
    }
    if (tenantMode.type === 'specialist') {
      return (
        <ErrorBoundary>
          <ThemeProvider>
            <div className="App">
              <Toaster richColors position="top-right" />
              <BrowserRouter>
                <Routes>
                  <Route path="/book/:linkId" element={<BookingPage />} />
                  <Route path="*" element={<ProfilePage slug={tenantMode.slug} />} />
                </Routes>
              </BrowserRouter>
            </div>
          </ThemeProvider>
        </ErrorBoundary>
      );
    }
  }

  // Main SaaS app
  return (
    <ErrorBoundary>
      <ThemeProvider>
      <IndustryProvider>
      <div className="App">
        <Toaster
          richColors
          position="top-right"
          toastOptions={{
            style: {
              background: 'var(--app-card)',
              border: '1px solid var(--app-border)',
              color: 'var(--app-text)',
            },
          }}
        />
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/verify-email" element={<VerifyEmailPage />} />
            <Route path="/recover" element={<RecoverPage />} />
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/queue" element={<LiveQueuePage />} />
            <Route path="/messenger" element={<MessengerPage />} />
            <Route path="/assistant" element={<AssistantPage />} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="/appointments" element={<AppointmentsPage />} />
            <Route path="/patients" element={<PatientsPage />} />
            <Route path="/finance" element={<FinancePage />} />
            <Route path="/analytics" element={<AnalyticsPage />} />
            <Route path="/patients/:patientId/board" element={<PatientBoardPage />} />
            <Route path="/files" element={<FilesPage />} />
            <Route path="/import" element={<ImportPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/profile-edit" element={<ProfileEditPage />} />
            <Route path="/admin" element={<AdminPage />} />
            <Route path="/p/:slug" element={<ProfilePage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/offer" element={<OfferPage />} />
            <Route path="/book/:linkId" element={<BookingPage />} />
            <Route path="/book" element={<BookingPage />} />
            <Route path="/organization/:slug" element={<ClinicPage />} />
            <Route path="/" element={<LandingPage />} />
          </Routes>
        </BrowserRouter>
      </div>
    </IndustryProvider>
    </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
